"use client";

import { useEffect, useState } from "react";
import { useTranslations } from "next-intl";
import { useInterval, useTimeout } from "usehooks-ts";

const FACT_KEYS = [
  "random_fact_1",
  "random_fact_2",
  "random_fact_3",
  "random_fact_4",
  "random_fact_5",
  "random_fact_6",
  "random_fact_7",
  "random_fact_8",
  "random_fact_9",
  "random_fact_10",
] as const;

const DEFAULT_DELAY_MS = 3000;
const DEFAULT_ROTATE_MS = 5000;

function pickRandomIndex(excludeIndex: number): number {
  let next: number;
  do {
    next = Math.floor(Math.random() * FACT_KEYS.length);
  } while (next === excludeIndex && FACT_KEYS.length > 1);
  return next;
}

/**
 * Returns a random fact from the translation keys, with a delayed reveal.
 * After the initial reveal, rotates to a new fact every `rotateMs`.
 */
export function useRandomFact(delayMs: number = DEFAULT_DELAY_MS, rotateMs: number = DEFAULT_ROTATE_MS) {
  const t = useTranslations("ClientKeys.RandomFact");
  const [factIndex, setFactIndex] = useState(0);

  // Randomize on mount to avoid hydration mismatch
  useEffect(() => {
    setFactIndex(Math.floor(Math.random() * FACT_KEYS.length));
  }, []);
  const [visible, setVisible] = useState(false);

  const fact = t(FACT_KEYS[factIndex]);
  const heading = t("random_fact_did_you_know");

  // Initial reveal delay
  useTimeout(() => setVisible(true), visible ? null : delayMs);

  // Rotate fact every rotateMs once visible
  useInterval(
    () => {
      setFactIndex(prev => pickRandomIndex(prev));
    },
    visible ? rotateMs : null,
  );

  return { fact, heading, visible };
}
